// Rules a tab keeps for blocks of its cells (Fixes 9), as Google Sheets has them:
//
// - checkboxes and dropdowns (data validation): a checkbox in each cell (TRUE ticked), or a dropdown of options, its
//   own list or the values of other cells, each shown as a chip in its colour. A value outside the list is kept as it
//   was typed and marked with a warning: a rule never refuses or rewrites what a person or an agent wrote;
// - colours by rule (conditional formatting): a fill and a text colour on the cells that meet a condition (empty, has
//   words, equals, greater than, a date before today), worked out again whenever a value changes.
//
// Both are kept on the tab (SheetTabData.valid, .cf), each with its block as a range ("B2:B49", "B:B", or "B2:B" down
// to the foot), and move with their cells when rows or columns are put in or taken out (lines.ts). The browser draws
// them and agents read and write them (agent.ts), from the same functions here.

import { MAX_ROWS, parseRect, type Rect } from "./addr";
import { PALETTE_SIZE } from "./format";
import { cleanRange } from "./names";
import { shiftFormula } from "./shift";
import { isErr, textNumber, type Scalar } from "./values";

/** Rules of each kind on a tab, options in a dropdown, and the characters of each. */
export const MAX_RULES = 50;
export const MAX_ITEMS = 200;
export const MAX_ITEM = 200;

export interface ValidRule {
  /** the cells it is on: "B2:B49", "B:B", "B2:B" (to the foot) */
  range: string;
  kind: "check" | "list";
  /** a dropdown's own options, in order */
  items?: string[];
  /** a dropdown whose options are the values of a block of cells: its tab (by id, so a rename keeps it) and range */
  from?: { tab: string; range: string };
  /** each option's chip colour, a palette place (1 to 8), by the option's words */
  colors?: Record<string, number>;
}

/** The conditions a colour rule (and a filter, Filter.tsx) can ask of a cell. */
export type When = "empty" | "not_empty" | "contains" | "not_contains" | "equals" | "not_equals" | "greater_than" | "less_than" | "before_today" | "today" | "after_today" | "date_before" | "date_after";
export const WHENS: When[] = ["empty", "not_empty", "contains", "not_contains", "equals", "not_equals", "greater_than", "less_than", "before_today", "today", "after_today", "date_before", "date_after"];
/** The conditions that need a value to compare with. */
export const WITH_VALUE: When[] = ["contains", "not_contains", "equals", "not_equals", "greater_than", "less_than", "date_before", "date_after"];

/** Each condition in plain words, as the dialog and an agent's read say it. */
export const WHEN_WORDS: Record<When, string> = {
  empty: "Is empty",
  not_empty: "Is not empty",
  contains: "Text contains",
  not_contains: "Text does not contain",
  equals: "Is equal to",
  not_equals: "Is not equal to",
  greater_than: "Is greater than",
  less_than: "Is less than",
  before_today: "Date is before today",
  today: "Date is today",
  after_today: "Date is after today",
  date_before: "Date is before",
  date_after: "Date is after",
};

export interface ColorRule {
  range: string;
  when: When;
  /** what it compares with: words, a number, or a date as the sheet types them */
  v?: string;
  /** the fill and text colour it gives, palette places (1 to 8) */
  bg?: number;
  fg?: number;
}

const place = (v: unknown): number | undefined => (typeof v === "number" && v >= 1 && v <= PALETTE_SIZE ? Math.round(v) : undefined);

/** An option's words, on one line, or null when there are none. */
export function cleanItem(v: unknown): string | null {
  if (typeof v !== "string" && typeof v !== "number" && typeof v !== "boolean") return null;
  const s = String(v).replace(/[\r\n\t]+/g, " ").replace(/\s+/g, " ").trim().slice(0, MAX_ITEM);
  return s || null;
}

/** A tab's checkboxes and dropdowns, each sound (cleanSheetTab); undefined for none. A rule whose cells were all deleted goes. */
export function cleanValid(raw: unknown): ValidRule[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: ValidRule[] = [];
  for (const x of raw) {
    if (out.length >= MAX_RULES) break;
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    const range = cleanRange(r.range);
    if (!range || range === "#REF!") continue;
    if (r.kind === "check") {
      out.push({ range, kind: "check" });
      continue;
    }
    if (r.kind !== "list") continue;
    const items: string[] = [];
    if (Array.isArray(r.items))
      for (const v of r.items) {
        const s = cleanItem(v);
        if (s && items.length < MAX_ITEMS && !items.some((i) => i.toLowerCase() === s.toLowerCase())) items.push(s);
      }
    const f = r.from as { tab?: unknown; range?: unknown } | null | undefined;
    const fr = f && typeof f === "object" ? cleanRange(f.range) : null;
    const from = f && typeof f.tab === "string" && f.tab && f.tab.length <= 40 && fr && fr !== "#REF!" ? { tab: f.tab, range: fr } : undefined;
    if (!items.length && !from) continue;
    const colors: Record<string, number> = {};
    if (r.colors && typeof r.colors === "object" && !Array.isArray(r.colors))
      for (const [k, v] of Object.entries(r.colors as Record<string, unknown>)) {
        const s = cleanItem(k);
        const c = place(v);
        if (s && c && Object.keys(colors).length < MAX_ITEMS) colors[s] = c;
      }
    out.push({ range, kind: "list", ...(from ? { from } : { items }), ...(Object.keys(colors).length ? { colors } : {}) });
  }
  return out.length ? out : undefined;
}

/** A tab's colour rules, each sound; undefined for none. */
export function cleanColorRules(raw: unknown): ColorRule[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: ColorRule[] = [];
  for (const x of raw) {
    if (out.length >= MAX_RULES) break;
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    const range = cleanRange(r.range);
    if (!range || range === "#REF!") continue;
    const when = WHENS.includes(r.when as When) ? (r.when as When) : null;
    if (!when) continue;
    const bg = place(r.bg);
    const fg = place(r.fg);
    if (!bg && !fg) continue;
    const v = WITH_VALUE.includes(when) ? (typeof r.v === "string" || typeof r.v === "number" ? String(r.v).replace(/[\r\n\t]+/g, " ").slice(0, MAX_ITEM) : "") : undefined;
    out.push({ range, when, ...(v !== undefined ? { v } : {}), ...(bg ? { bg } : {}), ...(fg ? { fg } : {}) });
  }
  return out.length ? out : undefined;
}

// ---------------------------------------------------------------------------
// which cells a rule is on

const RECTS = new Map<string, Rect | null>();

/** A rule's block, the whole of it (B2:B runs to the tab's last row). */
export function ruleRect(range: string): Rect | null {
  let x = RECTS.get(range);
  if (x === undefined) {
    x = parseRect(range);
    if (RECTS.size > 2000) RECTS.clear();
    RECTS.set(range, x);
  }
  return x;
}

/** Whether a range runs on to the tab's foot or edge (B:B, B2:B, 2:4): drawn only as far as the cells in use. */
export function isOpen(range: string): boolean {
  return !/^[A-Z]{1,3}[0-9]+(:[A-Z]{1,3}[0-9]+)?$/.test(range);
}

/** Whether a rule is on a cell. used: the tab's rows and columns in use, to draw an open range only that far. */
export function onCell(range: string, r: number, c: number, used?: { rows: number; cols: number }): boolean {
  const x = ruleRect(range);
  if (!x || r < x.r0 || r > x.r1 || c < x.c0 || c > x.c1) return false;
  if (used && isOpen(range)) return r < Math.max(used.rows, x.r0 + 1) && c < Math.max(used.cols, x.c0 + 1);
  return true;
}

/** The checkbox or dropdown on a cell: the last rule listed that holds it, as in Google Sheets the newest wins. */
export function validAt(rules: ValidRule[] | undefined, r: number, c: number, used?: { rows: number; cols: number }): ValidRule | null {
  if (!rules) return null;
  for (let i = rules.length - 1; i >= 0; i--) if (onCell(rules[i].range, r, c, used)) return rules[i];
  return null;
}

/**
 * A dropdown's options: its own list, or the words its block of cells shows (each once, in order, empty ones left
 * out). read gives what a tab's cells in a block show, row by row.
 */
export function optionsOf(rule: ValidRule, read?: (tabId: string, x: Rect) => string[]): string[] {
  if (rule.kind !== "list") return [];
  if (!rule.from) return rule.items ?? [];
  const x = ruleRect(rule.from.range);
  if (!x || !read) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s0 of read(rule.from.tab, x)) {
    const s = cleanItem(s0);
    if (!s || seen.has(s.toLowerCase())) continue;
    seen.add(s.toLowerCase());
    out.push(s);
    if (out.length >= MAX_ITEMS) break;
  }
  return out;
}

/** Whether a cell's value is one its rule takes: TRUE or FALSE for a checkbox, one of the options for a dropdown. An empty cell always is. */
export function fits(rule: ValidRule, v: Scalar, shown: string, options: string[]): boolean {
  if (v === null || v === "") return true;
  if (rule.kind === "check") return typeof v === "boolean";
  const s = shown.trim().toLowerCase();
  return options.some((o) => o.toLowerCase() === s);
}

/** An option's chip colour, by its words (any case). */
export function chipColor(rule: ValidRule, shown: string): number | undefined {
  if (!rule.colors) return undefined;
  const s = shown.trim().toLowerCase();
  for (const [k, n] of Object.entries(rule.colors)) if (k.toLowerCase() === s) return n;
  return undefined;
}

// ---------------------------------------------------------------------------
// colour rules

/**
 * Whether a cell meets a condition. v: its value; shown: its words as they show; date: whether it shows as a date;
 * today: today's day number where the user is; dmy: how the sheet reads a typed date (for date_before's value).
 */
export function meets(when: When, want: string | undefined, v: Scalar, shown: string, ctx: { today: number; dmy: boolean; date: boolean }): boolean {
  const empty = v === null || v === "";
  if (when === "empty") return empty;
  if (when === "not_empty") return !empty;
  if (empty || isErr(v)) return false;
  const w = (want ?? "").trim();
  const text = shown.toLowerCase();
  switch (when) {
    case "contains":
      return !!w && text.includes(w.toLowerCase());
    case "not_contains":
      return !w || !text.includes(w.toLowerCase());
    case "equals":
    case "not_equals": {
      const n = w ? textNumber(w, ctx.dmy) : null;
      const same = typeof v === "number" && n !== null ? Math.abs(v - n) < 1e-9 : text.trim() === w.toLowerCase() || (typeof v === "boolean" && String(v).toUpperCase() === w.toUpperCase());
      return when === "equals" ? same : !same;
    }
    case "greater_than":
    case "less_than": {
      const n = w ? textNumber(w, ctx.dmy) : null;
      if (typeof v !== "number" || n === null) return false;
      return when === "greater_than" ? v > n : v < n;
    }
    case "before_today":
    case "today":
    case "after_today": {
      if (typeof v !== "number" || !ctx.date) return false;
      const d = Math.floor(v);
      return when === "before_today" ? d < ctx.today : when === "today" ? d === ctx.today : d > ctx.today;
    }
    case "date_before":
    case "date_after": {
      if (typeof v !== "number" || !ctx.date) return false;
      const n = /^today$/i.test(w) ? ctx.today : w ? textNumber(w, ctx.dmy) : null;
      if (n === null) return false;
      return when === "date_before" ? Math.floor(v) < Math.floor(n) : Math.floor(v) > Math.floor(n);
    }
  }
  return false;
}

/** The colours the first rule a cell meets gives it, or null; the rules are tried in order, as in Google Sheets. */
export function colorFor(rules: ColorRule[] | undefined, r: number, c: number, used: { rows: number; cols: number }, read: () => { v: Scalar; shown: string; date: boolean }, ctx: { today: number; dmy: boolean }): { bg?: number; fg?: number } | null {
  if (!rules) return null;
  let cell: { v: Scalar; shown: string; date: boolean } | null = null;
  for (const rule of rules) {
    if (!onCell(rule.range, r, c, used)) continue;
    cell ??= read();
    if (meets(rule.when, rule.v, cell.v, cell.shown, { ...ctx, date: cell.date })) return { ...(rule.bg ? { bg: rule.bg } : {}), ...(rule.fg ? { fg: rule.fg } : {}) };
  }
  return null;
}

/** A rule in a line of words: "when empty", "when it contains “Done”". */
export function whenWords(rule: Pick<ColorRule, "when" | "v">): string {
  const w = WHEN_WORDS[rule.when].toLowerCase();
  // a number reads as a number, words in quotes
  return WITH_VALUE.includes(rule.when) ? `${w} ${/^-?\d+(\.\d+)?$/.test(rule.v ?? "") ? rule.v : `“${rule.v ?? ""}”`}` : w;
}

// ---------------------------------------------------------------------------
// rows and columns put in or taken out

/** Ranges of a tab's rules once rows or columns were put in or taken out there, as formulas' references move; a rule whose cells all went is gone. */
export function shiftRules<T extends { range: string }>(rules: T[] | undefined, tabName: string, l: { axis: "row" | "col"; at: number; n: number; del: boolean }, tabId?: string): T[] | null {
  if (!rules?.length) return null;
  const move = (range: string) => shiftFormula(`=${range}`, tabName, { tab: tabName, ...l }).slice(1);
  const out: T[] = [];
  for (const x of rules) {
    const range = move(x.range);
    if (range === "#REF!") continue;
    const from = (x as { from?: { tab: string; range: string } }).from;
    const next = from && tabId && from.tab === tabId ? { ...x, range, from: { ...from, range: move(from.range) } } : { ...x, range };
    out.push(next);
  }
  return out.length ? out : null;
}

/** Dropdowns on another tab whose options come from this one, their source moved by rows or columns put in or taken out here. */
export function shiftSources(rules: ValidRule[] | undefined, tabId: string, tabName: string, l: { axis: "row" | "col"; at: number; n: number; del: boolean }): ValidRule[] | null {
  if (!rules?.some((x) => x.from?.tab === tabId)) return null;
  return rules.map((x) => (x.from?.tab === tabId ? { ...x, from: { ...x.from, range: shiftFormula(`=${x.from.range}`, tabName, { tab: tabName, ...l }).slice(1) } } : x));
}

/** A whole column's range, from under the header when the tab has one: "B2:B". */
export function columnRange(c0: string, c1: string, from: number): string {
  return from > 0 ? `${c0}${from + 1}:${c1}` : `${c0}:${c1}`;
}

export { MAX_ROWS };
